import { Viewport } from "../pixel-accessor";
import type { Color } from "../types";
import type { ImageAdapter } from "./ImageAdapter";

/**
 * Adds virtual viewport offset support to ImageAdapter.
 */
export class VirtualViewportProxy<ResourceType>
  implements ImageAdapter<ResourceType>
{
  /**
   * @inheritDoc
   */
  readonly width: number;

  /**
   * @inheritDoc
   */
  readonly height: number;

  /**
   * Virtual viewport X offset.
   * @private
   */
  private offsetX: number;

  /**
   * Virtual viewport Y offset.
   * @private
   */
  private offsetY: number;

  /**
   * VirtualViewportProxy constructor.
   *
   * @param adapter
   */
  constructor(private readonly adapter: ImageAdapter<ResourceType>) {
    ({ x1: this.offsetX, y1: this.offsetY } = adapter.getViewport());
    ({ width: this.width, height: this.height } = adapter);
  }

  /**
   * Returns wrapped ImageAdapter.
   */
  getAdapter(): ImageAdapter<ResourceType> {
    return this.adapter;
  }

  /**
   * @inheritDoc
   */
  getPixelColor(x: number, y: number): Color {
    return this.adapter.getPixelColor(
      Math.floor(x - this.offsetX),
      Math.floor(y - this.offsetY)
    );
  }

  /**
   * @inheritDoc
   */
  setPixelColor(x: number, y: number, color: Color): void {
    this.adapter.setPixelColor(
      Math.floor(x - this.offsetX),
      Math.floor(y - this.offsetY),
      color
    );
  }

  /**
   * @inheritDoc
   */
  getViewport(): Viewport {
    return this.adapter.getViewport();
  }

  /**
   * @inheritDoc
   */
  setViewport(viewport: Viewport): void {
    this.adapter.setViewport(viewport);
    ({ x1: this.offsetX, y1: this.offsetY } = viewport);
  }

  /**
   * @inheritDoc
   */
  getBackgroundColor(): Color {
    return this.adapter.getBackgroundColor();
  }

  /**
   * @inheritDoc
   */
  setBackgroundColor(color: Color): void {
    this.adapter.setBackgroundColor(color);
  }

  /**
   * @inheritDoc
   */
  getAverageColor(): Color {
    return this.adapter.getAverageColor();
  }

  /**
   * @inheritDoc
   */
  getQuantumRange(): number {
    return this.adapter.getQuantumRange();
  }

  /**
   * @inheritDoc
   */
  getBlank(viewport: Viewport): ImageAdapter<ResourceType> {
    const blank = this.adapter.getBlank(viewport);
    return new VirtualViewportProxy(blank);
  }

  /**
   * @inheritDoc
   */
  scale(scale: number): ImageAdapter<ResourceType> {
    const scaled = this.adapter.scale(scale);
    return new VirtualViewportProxy(scaled);
  }

  /**
   * @inheritDoc
   */
  getResource(): ResourceType {
    return this.adapter.getResource();
  }

  /**
   * @inheritDoc
   */
  commit(): void {
    return this.adapter.commit();
  }
}
